// /home/coder/project/frontend/src/pages/ToCompEdit.tsx

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// highlight-start
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'; // Keep useQuery
import { useGetCountries } from '../../../hooks/genericHooks'; // Only useGetCountries needed from generic hooks
// highlight-end
import { Form, Card, Space, message, Typography, Row, Col, Input, Select, Spin, Alert } from 'antd';
import { SaveOutlined, RollbackOutlined, BankOutlined as CompanyIcon } from '@ant-design/icons';
// highlight-start
import { fetchSqlData, updateData } from '../../../services/api'; 
// highlight-end
import type { Company, CorCountries } from '../../../types'; // Import CorCountries
import Button from '../../../components/common/Button';

const { Title } = Typography;
const { Option } = Select;

// --- Relation Mapping and Helper ---
const relationOptions = [
  { value: 1, label: 'Root' }, { value: 2, label: 'Partner' }, { value: 3, label: 'Client' },
  { value: 11, label: 'Root Subcon' }, { value: 12, label: 'Root Vendor' },
  { value: 21, label: 'Partner Subcon' }, { value: 22, label: 'Partner Vendor' },
  { value: 33, label: 'Client Subcon' }, { value: 34, label: 'Client Vendor' },
  { value: 35, label: 'Other' },
];
// -----------------------------------

// Interface for form values, derived from Company type
interface CompanyFormValues {
  name: string;
  relation: number;
  country_code: string; // Added country code
  address?: string;
  phone?: string;
  email?: string;
  status: 'active' | 'inactive' | 'terminated';
  category: 'service' | 'goods' | 'both';
  area: 'Electric' | 'Mechanic' | 'Construction' | 'Design' | 'QAQC' | 'HSE' | 'PRECOMM' | 'Customs' | 'Transport' | 'Utility' | 'Administration' | 'Consultancy' | 'State Authority' | 'Other';
  short_code: string; // Added short_code
}

// --- Enum Options for Select dropdowns ---
const statusOptions: CompanyFormValues['status'][] = ['active', 'inactive', 'terminated'];
const categoryOptions: CompanyFormValues['category'][] = ['service', 'goods', 'both'];
const areaOptions: CompanyFormValues['area'][] = [
  'Electric', 'Mechanic', 'Construction', 'Design', 'QAQC', 'HSE', 'PRECOMM',
  'Customs', 'Transport', 'Utility', 'Administration', 'Consultancy',
  'State Authority', 'Other'
];
// -----------------------------------------

// Helper function to format enum strings (simple capitalize)
const formatEnumString = (value: string | undefined | null): string => {
    if (!value) return '-';
    const strValue = String(value);
    return strValue.charAt(0).toUpperCase() + strValue.slice(1);
};

// Helper function to get label from value for Relation
const getRelationLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  const option = relationOptions.find(opt => opt.value === value);
  return option ? option.label : String(value); // Return value as string if not found
};

const ToCompEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<CompanyFormValues>();
  const queryClient = useQueryClient();
  const companyId = id || '';

  // --- Data Fetching (Queries) ---
  // highlight-start
  // Fetch the specific company using useQuery directly
  const { data: companyDataResult, isLoading: isLoadingCompany, isError: isErrorCompany, error: errorCompany } = useQuery<Company[], Error>({
    queryKey: ['to_companies', companyId], // Unique key including the ID
    queryFn: () => fetchSqlData('to_companies', 'id', companyId),
    enabled: !!companyId, // Only run if companyId is truthy
  });
  // Assuming fetchSqlData returns an array even for a single ID match
  const companyDataArray = companyDataResult || [];
  // highlight-end

  // Fetch countries using the generic hook (this is correct)
  const { data: countriesData, isLoading: isLoadingCountries, isError: isErrorCountries, error: errorCountries } = useGetCountries();
  const countries = countriesData?.data || [];

  // Combine loading/error states
  const isLoading = isLoadingCompany || isLoadingCountries;
  const isError = isErrorCompany || isErrorCountries;
  const error = errorCompany || errorCountries; // Combine errors

  // --- Data Handling & Form Population ---
  useEffect(() => {
    // Ensure countries are loaded before setting form values that depend on them (like country_code)
    // Also ensure company data is loaded
    if (companyDataArray && companyDataArray.length > 0 && countries.length > 0) {
      const companyData = companyDataArray[0];
      console.log("Loaded company data (object):", companyData);

      form.setFieldsValue({
        name: companyData.name,
        relation: companyData.relation ?? undefined,
        country_code: companyData.country_code ?? undefined, // Populate country code
        address: companyData.address ?? undefined,
        phone: companyData.phone ?? undefined,
        email: companyData.email ?? undefined,
        status: statusOptions.includes(companyData.status as CompanyFormValues['status'])
          ? companyData.status as CompanyFormValues['status']
          : undefined,
        category: categoryOptions.includes(companyData.category as CompanyFormValues['category'])
          ? companyData.category as CompanyFormValues['category']
          : undefined,
        area: areaOptions.includes(companyData.area as CompanyFormValues['area'])
          ? companyData.area as CompanyFormValues['area']
          : undefined,
        short_code: companyData.short_code ?? undefined, // Set short_code
      });
    }
  }, [companyDataArray, countries, form]); // Add countries to dependency array

  // --- Mutation for Updating Data ---
  const updateCompanyMutation = useMutation({
    mutationFn: (updatedData: Partial<Company>) => updateData('to_companies', companyId, updatedData),
    onSuccess: () => {
      const companyName = companyDataArray?.[0]?.name || 'Company';
      message.success(`${companyName} updated successfully!`);
      queryClient.invalidateQueries({ queryKey: ['to_companies'] }); // Invalidate list
      queryClient.refetchQueries({ queryKey: ['to_companies'] }); // Force refresh persisted list
      queryClient.invalidateQueries({ queryKey: ['to_companies', companyId] }); // Invalidate specific company cache
      navigate('/to/companies');
    },
    onError: (error: any) => {
      console.error(`Error updating company:`, error);
      const errorMsg = error.response?.data?.error || error.message || 'An unknown error occurred.';
      message.error(`Error updating company: ${errorMsg}`);
    },
  });

  // --- Form Submission Logic ---
  const handleSubmit = (values: CompanyFormValues) => {
    const formattedData: Partial<Company> = {
      name: values.name,
      relation: values.relation,
      country_code: values.country_code, // Include country code
      address: values.address ?? undefined,
      phone: values.phone ?? undefined,
      email: values.email ?? undefined,
      status: values.status,
      category: values.category,
      area: values.area,
      short_code: values.short_code, // Include short_code
    };

    console.log("Submitting Updated Company Data:", JSON.stringify(formattedData, null, 2));
    updateCompanyMutation.mutate(formattedData);
  };

  const onFinish = (values: CompanyFormValues) => {
    handleSubmit(values);
  };

  // --- Conditional Rendering ---
  if (isLoading) return <Card><Spin tip="Loading data..." /></Card>; // Use combined loading state
  if (isError) {
      let combinedErrorMessage = 'Failed to load data: ';
      if (errorCompany) combinedErrorMessage += `Company (${errorCompany.message}) `;
      if (errorCountries) combinedErrorMessage += `Countries (${errorCountries.message}) `;
      console.error("Error fetching data:", error);
      message.error(combinedErrorMessage.trim());
      return (
        <Card>
          <Alert
            message="Error"
            description="Could not load required data. Please try again later."
            type="error"
            showIcon
          />
          <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')} style={{ marginTop: 16 }}>
            Back to List
          </Button>
        </Card>
      );
  }
  // Check specifically if the company wasn't found after loading completed
  if (!isLoadingCompany && (!companyDataArray || companyDataArray.length === 0)) {
      return (
        <Card>
          <Alert message={`Company with ID ${companyId} not found`} type="warning" showIcon />
          <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')} style={{ marginTop: 16 }}>
            Back to List
          </Button>
        </Card>
      );
  }

  // --- Define Form Item Components ---
  const nameItem = (
    <Form.Item name="name" label="Company Name" rules={[{ required: true, message: 'Please enter the company name' }]}>
      <Input placeholder="Enter company name" />
    </Form.Item>
  );
  const relationItem = (
    <Form.Item
      name="relation"
      label="Relation"
      rules={[{ required: true, message: 'Please select the relation type' }]}
    >
      <Select
        placeholder="Select relation type"
        allowClear={false}
        showSearch
        filterOption={(input, option) =>
          (String(option?.label ?? '')).toLowerCase().includes(input.toLowerCase())
        }
      >
        {relationOptions.map(opt => (
          <Option key={opt.value} value={opt.value} label={opt.label}>
            {opt.label}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
  const countryItem = (
     <Form.Item name="country_code" label="Country" rules={[{ required: true, message: 'Please select the country' }]}>
      <Select
        placeholder="Select country"
        loading={isLoadingCountries} // Show loading indicator on the select itself
        showSearch
        allowClear={false} // Assuming country is required
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        options={countries.map(country => ({
          value: country.country_code,
          label: country.name
        }))}
      />
    </Form.Item>
  );
  const addressItem = (
    <Form.Item name="address" label="Address">
      <Input.TextArea rows={2} placeholder="Enter company address" />
    </Form.Item>
  );
  const phoneItem = (
    <Form.Item name="phone" label="Phone">
      <Input placeholder="Enter phone number" />
    </Form.Item>
  );
  const emailItem = (
    <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Please enter a valid email' }]}>
      <Input placeholder="Enter email address" />
    </Form.Item>
  );
  const statusItem = (
    <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select the status' }]}>
      <Select placeholder="Select status" allowClear={false}>
        {statusOptions.map(status => (<Option key={status} value={status}>{formatEnumString(status)}</Option>))}
      </Select>
    </Form.Item>
  );
  const categoryItem = (
    <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please select the category' }]}>
      <Select placeholder="Select category" allowClear={false}>
        {categoryOptions.map(cat => (<Option key={cat} value={cat}>{formatEnumString(cat)}</Option>))}
      </Select>
    </Form.Item>
  );
  const areaItem = (
    <Form.Item name="area" label="Area" rules={[{ required: true, message: 'Please select the area' }]}>
      <Select
        placeholder="Select area of occupation"
        allowClear={false}
        showSearch
        filterOption={(input, option) =>
          (String(option?.label ?? '')).toLowerCase().includes(input.toLowerCase())
        }
      >
        {areaOptions.map(area => (
          <Option key={area} value={area} label={formatEnumString(area)}>
            {formatEnumString(area)}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
  const shortCodeItem = (
    <Form.Item
      name="short_code"
      label="Short Code"
      rules={[
        { required: true, message: 'Please enter the 3-letter short code' },
        { len: 3, message: 'Short code must be exactly 3 characters' },
        { pattern: /^[A-Za-z0-9]+$/, message: 'Short code must be alphanumeric' },
      ]}
    >
      <Input placeholder="e.g. ABC" maxLength={3} />
    </Form.Item>
  );
  // --- End of Form Item Definitions ---

  // --- Main Component Return ---
  return (
    <Card>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <CompanyIcon style={{ marginRight: 8 }} />
            {/* Use data from the potentially loaded array */}
            Edit Company: {companyDataArray?.[0]?.name || ''}
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/companies')} disabled={updateCompanyMutation.isPending}>
              Back to List
            </Button>
            <Button action="edit" type="primary" icon={<SaveOutlined />} loading={updateCompanyMutation.isPending} onClick={() => form.submit()}>
              Save Changes
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Form */}
      <Form form={form} layout="vertical" onFinish={onFinish} disabled={isLoading || updateCompanyMutation.isPending}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>{nameItem}</Col>
          <Col xs={24} sm={12} md={8}>{relationItem}</Col>
          <Col xs={24} sm={12} md={8}>{countryItem}</Col> {/* Added Country */}
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>{phoneItem}</Col>
          <Col xs={24} sm={12} md={8}>{emailItem}</Col>
          <Col xs={24} sm={12} md={8}>{statusItem}</Col> {/* Moved Status here */}
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>{categoryItem}</Col>
          <Col xs={24} sm={12} md={8}>{areaItem}</Col>
          <Col xs={24} sm={12} md={8}>{shortCodeItem}</Col> {/* Added Short Code input here */}
        </Row>
        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col xs={24} sm={24} md={8}>{addressItem}</Col> {/* Moved Address here */}
        </Row>
      </Form>
    </Card>
  );
};

export default ToCompEdit;
